import { useTranslation } from '../../../../lib/i18n';
import { useConversationDecisions } from '../../../../lib/use-conversation-decisions';
import { DecisionAnswerBlock } from '../../goals/DecisionAnswerBlock';
import { Typography } from '../../ui';
import styles from './BriefOpenDecisions.module.css';

/**
 * The questions THIS meeting raised, still waiting on you — answerable where they were asked.
 *
 * The brief has always been able to raise a decision (`presentedTurnId` is stamped `brief-…`) and never
 * been able to take an answer to one: it rendered the question as prose and ended in a composer. So the
 * reader answered in chat, the turn resolved, the row stayed OPEN, and the next morning asked again.
 * Measured on a real account — a channel choice answered on Sep 5, the outreach sent 62 seconds later,
 * and the brief still calling it "waiting on your go-ahead" on Sep 9.
 *
 * The control is `DecisionAnswerBlock`, the SAME one the goal panel uses, on the same
 * `POST /api/decision-verdict` write path. That is deliberate and is the point of the change: this bug
 * exists because there were two ways to answer a question and only one of them wrote anything down.
 * A second control here would be a third way.
 *
 * Renders NOTHING when there is nothing open — no heading, no empty state. The brief is a thing you act
 * on this morning; a card announcing "no questions" is furniture, and on the many mornings that raise
 * none it would be the only thing this section ever showed.
 */
export function BriefOpenDecisions({ conversationId }: { conversationId: string | undefined }) {
  const { t } = useTranslation();
  const decisions = useConversationDecisions(conversationId);
  if (decisions.length === 0) {
    return null;
  }
  return (
    <section className={styles.wrap} data-testid="briefOpenDecisions">
      <Typography variant="h4" component="h4" color="strong" className={styles.heading}>
        {t('goals.ledger.open.title')}
      </Typography>
      {decisions.map((row, index) => {
        const questionId = `briefDecision-${index}-question`;
        return (
          <div key={row.decision.id} className={styles.row} data-testid={`briefDecision-${index}`}>
            <span id={questionId} className={styles.question} dir="auto">
              {row.decision.question}
            </span>
            <DecisionAnswerBlock
              decision={row.decision}
              goalId={row.goalId}
              parentGoalId={row.parentGoalId}
              questionId={questionId}
              index={index}
            />
          </div>
        );
      })}
    </section>
  );
}
